import React, { useCallback, useRef, useEffect } from "react";
import styled from "styled-components";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { BackBarButtonMini } from "../../common/backBarButton";
import { MoviesSlider } from "./categoryList";
import smoothScroll from "../../../utils/animateScroll";
import {
  GLOBAL_COLORS,
  GLOBAL_FONT_SIZES,
  GLOBAL_SPACING,
  GLOBAL_RADIUS,
  FONT_FAMILY,
} from "../../../config/globalStyles";
import { useInputMode } from "../../../utils/hooks/useInputMode";

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

var ContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: table;
  font-family: ${FONT_FAMILY};
`;

var ContentInner = styled.div`
  display: table-cell;
  vertical-align: top;
  padding: ${GLOBAL_SPACING.lg} ${GLOBAL_SPACING.xxl};
  padding-bottom: 0;
`;

var ScrollContainer = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
`;

var HeaderRow = styled.div`
  display: table;
  width: 100%;
  margin-bottom: ${GLOBAL_SPACING.lg};
`;

var HeaderCell = styled.div`
  display: table-cell;
  vertical-align: middle;
`;

var PageTitle = styled.h4`
  color: ${GLOBAL_COLORS.text};
  font-size: ${GLOBAL_FONT_SIZES.title};
  font-weight: 700;
  margin: 0;
  margin-right: ${GLOBAL_SPACING.md};
  display: inline-block;
`;

var ContentContainer = styled.div`
padding-bottom: 5rem;
`;

var EmptyState = styled.div`
  background: ${GLOBAL_COLORS.cardBg};
  color: ${GLOBAL_COLORS.text};
  padding: ${GLOBAL_SPACING.lg};
  border-radius: ${GLOBAL_RADIUS.lg};
  font-size: ${GLOBAL_FONT_SIZES.body};
`;

var Sentinel = styled.div`
  height: 1px;
`;

var LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 3.5rem;
  color: ${GLOBAL_COLORS.text};
  font-size: ${GLOBAL_FONT_SIZES.body};
  font-family: ${FONT_FAMILY};
`;

// ============================================================================
// MAIN COMPONENT
// ============================================================================

var GridContent = function (props) {
  const { data, title, onLoadMore, hasMore, isLoading } = props;
  var { ref, focusKey } = useFocusable();
  var innerRef = useRef(null);
  var sentinelRef = useRef(null);
  var { isMouseFocus } = useInputMode();

  var onRowFocus = useCallback(
    function (focusProps) {
      if (isMouseFocus) return;
      var y = focusProps.y;
      var height = focusProps.height;
      var targetScroll = y > height - 200 ? y - 200 : 0;
      smoothScroll(ref, targetScroll, 500);
    },
    [ref, isMouseFocus]
  );

  // IntersectionObserver for infinite scroll
  useEffect(
    function () {
      var observer = new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          if (entry.isIntersecting && hasMore && !isLoading && onLoadMore) {
            onLoadMore();
          }
        },
        { root: ref.current, threshold: 1.0 }
      );

      var sentinel = sentinelRef.current;
      if (sentinel) {
        observer.observe(sentinel);
      }

      return function () {
        if (sentinel) {
          observer.unobserve(sentinel);
        }
      };
    },
    [hasMore, isLoading, onLoadMore, ref]
  );

  return (
    <ContentWrapper>
      <ContentInner>
        <FocusContext.Provider value={focusKey}>
          <ScrollContainer ref={ref}>
            <div>
              <HeaderRow>
                <HeaderCell>
                  <BackBarButtonMini />
                  <PageTitle>{title}</PageTitle>
                </HeaderCell>
              </HeaderRow>
              <ContentContainer ref={innerRef}>
                {data && data.length > 0 ? (
                  <MoviesSlider
                    movies={data}
                    onFocus={onRowFocus}
                  />
                ) : (
                  <EmptyState>
                    <p>محتوایی یافت نشد</p>
                  </EmptyState>
                )}
                <Sentinel ref={sentinelRef} />
                {isLoading && (
                  <LoadingIndicator>
                    <span>در حال بارگذاری...</span>
                  </LoadingIndicator>
                )}
              </ContentContainer>
            </div>
          </ScrollContainer>
        </FocusContext.Provider>
      </ContentInner>
    </ContentWrapper>
  );
};

export default GridContent;

